import * as helpers from '../helpers';

/**
 Direction aware content reveals.

 @url https://github.com/NigelOToole/direction-reveal
 @param {Object} options - Container for all options.
 @param {string} selector - Container element selector.
 @param {string} itemSelector - Item element selector.
 @param {string} animationName - Animation CSS class.
 @param {boolean} enableTouch  - Adds touch event to show content on first click then follow link on the second click.
 @param {integer} touchThreshold - Touch length must be less than this to trigger reveal which prevents the event triggering if user is scrolling.
 */
export default class DirectionAware {
    constructor(selector = '.js-direction', options = null) {
        this.selector = selector;
        this.options = {
            itemSelector: 'a',
            animationName: 'slide',
            enableTouch: true,
            touchThreshold: 300,
        };
        this.touchStart = '';

        if (options instanceof Object) {
            this.options = helpers.extendDefaults(this.options, options);
        }

        this.translateDirection = helpers.switchcase({
            0: 'top',
            1: 'right',
            2: 'bottom',
            3: 'left',
        })('top');

        if (typeof this.selector !== 'string') {
            this.nodes = [].slice.call(this.selector.querySelectorAll(this.options.itemSelector), 0);
            this.initEvents();

            const svg = this.selector.querySelectorAll('svg');
            [...svg].forEach((el) => {
                el.setAttribute('focusable', 'false');
            });
        }

        if (typeof selector === 'string') {
            const els = document.querySelectorAll(selector);
            for (let i = 0; i < els.length; i++) {
                const el = new DirectionAware(els[i], this.options);
            }
        }
    }

    getDirection(e, item) {
        // Width and height of current item
        const w = item.offsetWidth;
        const h = item.offsetHeight;
        const position = this.getPosition(item);

        // Calculate the x/y value of the pointer entering/exiting, relative to the center of the item.
        const x = (e.pageX - position.x - ((w / 2) * (w > h ? (h / w) : 1)));
        const y = (e.pageY - position.y - ((h / 2) * (h > w ? (w / h) : 1)));

        // console.table([x, y, w, h, e.pageX, e.pageY, item.offsetLeft, item.offsetTop, position.x, position.y]);

        // Calculate the angle the pointer entered/exited and convert to clockwise format (top/right/bottom/left = 0/1/2/3).
        // See https://stackoverflow.com/a/3647634 for a full explanation.
        return Math.round((Math.atan2(y, x) / 1.57079633) + 5) % 4;
    }

    // https://www.kirupa.com/html5/get_element_position_using_javascript.htm
    getPosition(el) {
        let xPos = 0;
        let yPos = 0;
        let path = el;

        while (path) {
            xPos += (path.offsetLeft + path.clientLeft);
            yPos += (path.offsetTop + path.clientTop);

            path = path.offsetParent;
        }
        return {
            x: xPos,
            y: yPos,
        };
    }

    addClass(e, state) {
        const currentItem = e.currentTarget;
        const direction = this.getDirection(e, currentItem);
        const directionString = this.translateDirection(direction);

        // Remove current animation classes and add new ones e.g. swap -in for -out.
        const currentCssClasses = currentItem.className.split(' ');
        currentItem.className = currentCssClasses.filter(cssClass => (!cssClass.startsWith(`-${this.options.animationName}`))).join(' ');
        currentItem.classList.add(`-${this.options.animationName}-${state}-${directionString}`);
    }

    initEvents() {
        this.nodes.forEach((item) => {
            this.addEventListenerMulti(item, ['mouseenter', 'focus'], (e) => {
                this.addClass(e, 'in');
            });

            this.addEventListenerMulti(item, ['mouseleave', 'blur'], (e) => {
                this.addClass(e, 'out');
            });

            if (this.options.enableTouch) {
                item.addEventListener('touchstart', (e) => {
                    this.touchStart = +new Date();
                }, {passive: true});

                item.addEventListener('touchend', (e) => {
                    const touchTime = +new Date() - this.touchStart;

                    if (touchTime < this.options.touchThreshold && !item.className.includes(`-${this.options.animationName}-in`)) {
                        e.preventDefault();

                        this.resetVisible(e, this.nodes);
                    }
                });
            }
        });
    }

    addEventListenerMulti(element, events, fn) {
        events.forEach(e => element.addEventListener(e, fn));
    }

    resetVisible(e, items) {
        items.forEach((item) => {
            const currentCssClasses = item.className;

            if (currentCssClasses.includes(`-${this.options.animationName}-in`) && item !== e.currentTarget) {
                item.className = currentCssClasses.replace(`-${this.options.animationName}-in`, `-${this.options.animationName}-out`);
            }
        });

        this.addClass(e, 'in');
    }
}
